import type { FormQuestion, FormResponse } from "../../types/form";
import type {
  InvestorAlphaBrokerageChoice,
  InvestorAlphaPreference,
} from "../../types/investorAlpha";
import {
  getIbkrAccountQuestion,
  getSavedIbkrAccountChoice,
} from "./alphaBrokerageAccount";
import {
  getIbkrSetupCompletionQuestion,
  getSavedIbkrSetupCompletion,
  type SavedIbkrSetupCompletion,
} from "./alphaIbkrSetupCompletion";
import {
  areAlphaIbkrAccountDetailsComplete,
  getAlphaIbkrAccountDetailQuestionIds,
  getSavedAlphaIbkrAccountDetails,
  type AlphaIbkrAccountType,
} from "./alphaIbkrAccountDetails";
import {
  getInvestmentTrackQuestion,
  getSavedInvestmentTrackPreference,
} from "./alphaInvestmentTrack";
import {
  getAlphaRiskProfileQuestionIds,
  getCompleteSavedAlphaRiskProfile,
  type AlphaRiskProfileAnswers,
} from "./alphaRiskProfile";

export interface AlphaProgressResponses {
  investmentTrackPreference: InvestorAlphaPreference | null;
  ibkrAccountChoice: InvestorAlphaBrokerageChoice | null;
  ibkrSetupCompletion: SavedIbkrSetupCompletion | null;
  ibkrAccountName: string | null;
  ibkrAccountType: AlphaIbkrAccountType | null;
  riskProfile: AlphaRiskProfileAnswers | null;
}

const responsesForQuestion = (
  responses: FormResponse[],
  questionId: number
): FormResponse[] => responses.filter((response) => response.questionId === questionId);

const resolveInvestmentTrackPreference = (
  questions: FormQuestion[],
  responses: FormResponse[]
): InvestorAlphaPreference | null => {
  try {
    const question = getInvestmentTrackQuestion(questions);
    return getSavedInvestmentTrackPreference(
      question,
      responsesForQuestion(responses, question.id)
    );
  } catch {
    return null;
  }
};

const resolveIbkrAccountChoice = (
  questions: FormQuestion[],
  responses: FormResponse[]
): InvestorAlphaBrokerageChoice | null => {
  try {
    const question = getIbkrAccountQuestion(questions);
    return getSavedIbkrAccountChoice(
      question,
      responsesForQuestion(responses, question.id)
    );
  } catch {
    return null;
  }
};

const resolveIbkrSetup = (
  questions: FormQuestion[],
  responses: FormResponse[],
  savedAccountChoice: InvestorAlphaBrokerageChoice | null
): {
  completion: SavedIbkrSetupCompletion | null;
  accountName: string | null;
  accountType: AlphaIbkrAccountType | null;
} => {
  try {
    const question = getIbkrSetupCompletionQuestion(questions);
    const savedCompletion = getSavedIbkrSetupCompletion(
      question.id,
      responsesForQuestion(responses, question.id)
    );
    if (!savedCompletion) {
      return { completion: null, accountName: null, accountType: null };
    }
    const expectedAccountChoice = savedCompletion.response === "yes-existing"
      ? "existing"
      : "new";
    if (savedAccountChoice !== expectedAccountChoice) {
      return { completion: null, accountName: null, accountType: null };
    }

    const detailQuestionIds = getAlphaIbkrAccountDetailQuestionIds(questions);
    const savedDetails = getSavedAlphaIbkrAccountDetails(
      detailQuestionIds,
      responses
    );
    if (!areAlphaIbkrAccountDetailsComplete(savedDetails)) {
      return { completion: null, accountName: null, accountType: null };
    }
    return {
      completion: savedCompletion,
      accountName: savedDetails.accountName,
      accountType: savedDetails.accountType ?? null,
    };
  } catch {
    return { completion: null, accountName: null, accountType: null };
  }
};

const resolveRiskProfile = (
  questions: FormQuestion[],
  responses: FormResponse[]
): AlphaRiskProfileAnswers | null => {
  try {
    const questionIds = getAlphaRiskProfileQuestionIds(questions);
    return getCompleteSavedAlphaRiskProfile(questions, questionIds, responses);
  } catch {
    return null;
  }
};

export function getAlphaProgressResponses(
  questions: FormQuestion[],
  responses: FormResponse[]
): AlphaProgressResponses {
  const ibkrAccountChoice = resolveIbkrAccountChoice(questions, responses);
  const ibkrSetup = resolveIbkrSetup(questions, responses, ibkrAccountChoice);
  return {
    investmentTrackPreference: resolveInvestmentTrackPreference(questions, responses),
    ibkrAccountChoice,
    ibkrSetupCompletion: ibkrSetup.completion,
    ibkrAccountName: ibkrSetup.accountName,
    ibkrAccountType: ibkrSetup.accountType,
    riskProfile: resolveRiskProfile(questions, responses),
  };
}
